import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { showToastNotification, toFixedThree } from "../../utility/helper";
import InputField from "../../components/UI/Forms/InputField";
import ErrorMessage from "../../components/UI/Notifications/ErrorMessage";
import Button from "../../components/UI/Buttons/Button";
import ConfirmationModal from "../../components/UI/Modal/ConfirmationModal";
import useConfirmationModal from "../../hooks/useConfirmationModal";
import useImageUpload from "../../hooks/useImageUpload";
import useFileDelete from "../../hooks/useFileDelete";
import ProductVariantSelector from "../../components/common/ProductVariantSelector";
import { IModifierOption } from "../../interfaces/modifier";
import { IProduct, ProductInfo } from "../../interfaces/product";

interface ModifierOptionsProps {
    modifierGroupId: string;
    options: IModifierOption[];
    onOptionAdded: () => void;
}

type Method = "standalone" | "linked";

const ModifierOptions: React.FC<ModifierOptionsProps> = ({ modifierGroupId, options, onOptionAdded }) => {
    const [method, setMethod] = useState<Method>("standalone");
    const [editingOption, setEditingOption] = useState<IModifierOption | null>(null);
    const { showModal, modalProps, confirmAction } = useConfirmationModal();
    const { deleteFile } = useFileDelete();

    const isEditing = !!editingOption;
    const isEditingLinked = !!editingOption?.productId;

    /* ---------------- Standalone option ---------------- */
    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm<any>({ mode: "onBlur", defaultValues: { trackInventory: true, lowStockThreshold: 1 } });
    const { uploadImage, isUploading } = useImageUpload();
    const [standaloneImage, setStandaloneImage] = useState<string>("");
    const [savingStandalone, setSavingStandalone] = useState(false);

    const handleStandaloneImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 1 * 1024 * 1024) {
            return showToastNotification("danger", `"${file.name}" exceeds 1MB limit.`);
        }
        try {
            const url = await uploadImage(file);
            setStandaloneImage(url);
        } catch (err) {
            console.error(err);
        }
    };

    const removeStandaloneImage = async () => {
        // Only physically delete freshly uploaded images, never the one already saved on the option
        if (standaloneImage && standaloneImage !== editingOption?.imageUrl) await deleteFile(standaloneImage);
        setStandaloneImage("");
    };

    const onSubmitStandalone = async (data: any) => {
        setSavingStandalone(true);
        const payload: any = {
            name: data.name,
            imageUrl: standaloneImage,
            price: data.price,
            weight: data.weight ?? "",
            inventoryQuantity: data.inventoryQuantity ? Number(data.inventoryQuantity) : 0,
            lowStockThreshold: data.lowStockThreshold ? Number(data.lowStockThreshold) : 1,
            // Backend defaults for fields not exposed in the UI
            isActive: editingOption?.isActive ?? true,
            isDefault: editingOption?.isDefault ?? false,
            sortOrder: editingOption?.sortOrder ?? 0,
            trackInventory: true,
        };
        try {
            if (editingOption) {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/options/${editingOption.id}`, {
                    method: "PATCH",
                    data: payload,
                });
                showToastNotification("success", "Modifier option updated successfully");
            } else {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/options`, {
                    method: "POST",
                    data: { modifierGroupId, ...payload },
                });
                showToastNotification("success", "Modifier option added successfully");
            }
            resetStandalone();
            onOptionAdded();
        } catch (error: any) {
            showToastNotification("danger", error.message || "Failed to save option");
        } finally {
            setSavingStandalone(false);
        }
    };

    const resetStandalone = () => {
        reset({ name: "", price: "", weight: "", inventoryQuantity: "", trackInventory: true, lowStockThreshold: 1 });
        setStandaloneImage("");
        setEditingOption(null);
    };

    /* ---------------- Product linked option ---------------- */
    const [linkedProduct, setLinkedProduct] = useState<IProduct | null>(null);
    const [linkedVariant, setLinkedVariant] = useState<ProductInfo | null>(null);
    const [nameOverride, setNameOverride] = useState<string>("");
    const [priceOverride, setPriceOverride] = useState<string>("");
    const [imageOverride, setImageOverride] = useState<string>("");
    const [linkedInventoryQuantity, setLinkedInventoryQuantity] = useState<string>("");
    const [linkedWeight, setLinkedWeight] = useState<string>("");
    const [savingLinked, setSavingLinked] = useState(false);

    const handleVariantSelect = (product: IProduct | null, variant: ProductInfo | null) => {
        setLinkedProduct(product);
        setLinkedVariant(variant);
        if (product && variant) {
            // Auto-fill overrides from the selected variant (editable before saving)
            setNameOverride(product.title || "");
            setPriceOverride(variant.price?.toString() ?? "");
            setImageOverride(product.images?.[0] ?? "");
            setLinkedInventoryQuantity(variant.quantity?.toString() ?? "");
            setLinkedWeight(variant.pieceWeight ?? "");
        } else if (!product) {
            setNameOverride("");
            setPriceOverride("");
            setImageOverride("");
            setLinkedInventoryQuantity("");
            setLinkedWeight("");
        }
    };

    const resetLinked = () => {
        setLinkedProduct(null);
        setLinkedVariant(null);
        setNameOverride("");
        setPriceOverride("");
        setImageOverride("");
        setLinkedInventoryQuantity("");
        setLinkedWeight("");
        setEditingOption(null);
    };

    const onSubmitLinked = async () => {
        // On create we require a product + variant; on edit the link is fixed and only overrides change.
        if (!editingOption && (!linkedProduct || !linkedVariant)) {
            return showToastNotification("danger", "Please select a product and a variant.");
        }
        setSavingLinked(true);
        const overrides = {
            priceOverride,
            nameOverride,
            imageOverride,
            inventoryQuantity: linkedInventoryQuantity ? Number(linkedInventoryQuantity) : 0,
            weight: linkedWeight,
        };
        try {
            if (editingOption) {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/options/${editingOption.id}`, {
                    method: "PATCH",
                    data: {
                        ...overrides,
                        isActive: editingOption.isActive ?? true,
                        isDefault: editingOption.isDefault ?? false,
                        sortOrder: editingOption.sortOrder ?? 0,
                        trackInventory: true,
                    },
                });
                showToastNotification("success", "Product linked option updated successfully");
            } else {
                await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/options`, {
                    method: "POST",
                    data: {
                        modifierGroupId,
                        productId: linkedProduct!.id,
                        productInfoId: linkedVariant!.id,
                        name: linkedProduct!.title,
                        ...overrides,
                        isActive: true,
                        isDefault: false,
                        sortOrder: 0,
                    },
                });
                showToastNotification("success", "Product linked option added successfully");
            }
            resetLinked();
            onOptionAdded();
        } catch (error: any) {
            showToastNotification("danger", error.message || "Failed to save option");
        } finally {
            setSavingLinked(false);
        }
    };

    /* ---------------- Edit / delete ---------------- */
    const handleEdit = (option: IModifierOption) => {
        setEditingOption(option);
        if (option.productId) {
            setMethod("linked");
            setLinkedProduct(option.product ?? null);
            setLinkedVariant(option.productInfo ?? null);
            setNameOverride(option.nameOverride ?? "");
            setPriceOverride(option.priceOverride ?? "");
            setImageOverride(option.imageOverride ?? "");
            setLinkedInventoryQuantity(option.inventoryQuantity?.toString() ?? "");
            setLinkedWeight(option.weight ?? option.productInfo?.pieceWeight ?? "");
        } else {
            setMethod("standalone");
            reset({
                name: option.name ?? "",
                price: option.price ?? "",
                weight: option.weight ?? "",
                inventoryQuantity: option.inventoryQuantity ?? "",
                trackInventory: option.trackInventory ?? true,
                lowStockThreshold: option.lowStockThreshold ?? 1,
            });
            setStandaloneImage(option.imageUrl ?? "");
        }
        window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    };

    const handleCancelEdit = () => {
        if (method === "linked") resetLinked();
        else resetStandalone();
    };

    const deleteOption = async (option: IModifierOption) => {
        try {
            await callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/options/${option.id}`, { method: "DELETE" });
            showToastNotification("success", "Modifier option deleted successfully");
            if (editingOption?.id === option.id) handleCancelEdit();
            onOptionAdded();
        } catch (error: any) {
            showToastNotification("danger", error.message || "Failed to delete option");
        }
    };

    const handleDeleteClick = (option: IModifierOption) => {
        confirmAction({
            title: "Confirm Delete",
            message: "Are you sure you want to delete this option?",
            onConfirm: () => deleteOption(option),
        });
    };

    /* ---------------- Helpers ---------------- */
    const optionName = (o: IModifierOption) => o.nameOverride || o.name || o.product?.title || "-";
    const optionImage = (o: IModifierOption) => o.imageOverride || o.imageUrl || o.product?.images?.[0];
    const optionPrice = (o: IModifierOption) => o.priceOverride || o.price;
    const optionInventory = (o: IModifierOption) => o.inventoryQuantity ?? o.productInfo?.quantity ?? "-";
    const optionWeight = (o: IModifierOption) => o.weight || o.productInfo?.pieceWeight || "-";

    const renderPrice = (o: IModifierOption) => {
        const price = optionPrice(o);
        if (price === undefined || price === null || price === "") return "-";
        if (Number(price) === 0) return <span className="badge bg-success">Free</span>;
        return toFixedThree(price as string);
    };

    return (
        <div>
            {/* Existing options */}
            <div className="table-responsive mb-4">
                <table className="table app-table-hover mb-0 text-left align-middle">
                    <thead>
                        <tr>
                            <th className="cell text-nowrap">Image</th>
                            <th className="cell text-nowrap">Name</th>
                            <th className="cell text-nowrap">Price (BHD)</th>
                            <th className="cell text-nowrap">Weight</th>
                            <th className="cell text-nowrap">Inventory</th>
                            <th className="cell text-nowrap">Type</th>
                            <th className="cell text-nowrap">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {options.length === 0 ? (
                            <tr>
                                <td className="text-center" colSpan={7}>
                                    No options added yet.
                                </td>
                            </tr>
                        ) : (
                            options.map((o) => (
                                <tr key={o.id} className={editingOption?.id === o.id ? "table-active" : ""}>
                                    <td className="cell">
                                        <div className="table-image">
                                            {optionImage(o) ? (
                                                <img
                                                    className="img-image"
                                                    src={optionImage(o)}
                                                    alt=""
                                                    style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover" }}
                                                />
                                            ) : (
                                                <span className="text-muted">-</span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="cell">{optionName(o)}</td>
                                    <td className="cell text-nowrap">{renderPrice(o)}</td>
                                    <td className="cell text-nowrap">{optionWeight(o)}</td>
                                    <td className="cell text-nowrap">{optionInventory(o)}</td>
                                    <td className="cell">
                                        <span className={`badge text-nowrap ${o.productId ? "bg-info" : "bg-secondary"}`}>
                                            {o.productId ? "Product Linked" : "Standalone"}
                                        </span>
                                    </td>
                                    <td className="cell">
                                        <div className="d-flex align-items-center gap-1">
                                            <button
                                                type="button"
                                                className="btn btn-success px-2 py-1 text-white"
                                                onClick={() => handleEdit(o)}
                                            >
                                                <i className="bi bi-pen" />
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger px-2 py-1 text-white"
                                                onClick={() => handleDeleteClick(o)}
                                            >
                                                <i className="bi bi-trash3" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Add / edit option */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <h6 className="fw-bold mb-0">{isEditing ? "Edit Option" : "Add Option"}</h6>
                {isEditing && (
                    <button type="button" className="btn btn-outline-secondary btn-sm text-nowrap" onClick={handleCancelEdit}>
                        <i className="bi bi-x-lg me-1" /> Cancel Edit
                    </button>
                )}
            </div>

            {/* Method switch is only for creating a new option */}
            {!isEditing && (
                <ul className="nav nav-pills mb-3 gap-2">
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${method === "standalone" ? "active" : ""}`}
                            onClick={() => setMethod("standalone")}
                        >
                            <i className="bi bi-box me-1" /> Standalone Option
                        </button>
                    </li>
                    <li className="nav-item">
                        <button
                            type="button"
                            className={`nav-link ${method === "linked" ? "active" : ""}`}
                            onClick={() => setMethod("linked")}
                        >
                            <i className="bi bi-link-45deg me-1" /> Product Linked Option
                        </button>
                    </li>
                </ul>
            )}

            {method === "standalone" ? (
                /* Standalone — onSubmit handled locally, not the parent form */
                <div className="row">
                    <div className="mb-3 col-12 col-sm-6 col-md-4">
                        <InputField
                            type="text"
                            register={register("name", { required: "Name is required" })}
                            name="name"
                            label="Name"
                            placeholder="Enter option name"
                        />
                        <ErrorMessage message={errors.name?.message?.toString()} />
                    </div>
                    <div className="mb-3 col-12 col-sm-6 col-md-4">
                        <InputField
                            type="number"
                            register={register("price", { required: "Price is required" })}
                            name="price"
                            label="Price (BHD)"
                            placeholder="Enter price (0 = Free)"
                            step="any"
                        />
                        <ErrorMessage message={errors.price?.message?.toString()} />
                    </div>
                    <div className="mb-3 col-12 col-sm-6 col-md-4">
                        <InputField
                            type="text"
                            register={register("weight")}
                            name="weight"
                            label="Weight"
                            placeholder="e.g. 200 GRAMS"
                        />
                    </div>

                    <div className="mb-3 col-12 col-sm-6 col-md-4">
                        <InputField
                            type="number"
                            register={register("inventoryQuantity")}
                            name="inventoryQuantity"
                            label="Inventory Quantity"
                            placeholder="Enter inventory quantity"
                        />
                    </div>
                    {/* <div className="mb-3 col-12 col-sm-6 col-md-4">
                        <InputField
                            type="number"
                            register={register("lowStockThreshold")}
                            name="lowStockThreshold"
                            label="Low Stock Threshold"
                            placeholder="Enter low stock threshold"
                        />
                    </div> */}
                    {/* <div className="mb-3 col-md-4 d-flex align-items-center">
                        <div className="form-check mt-3">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="trackInventory"
                                {...register("trackInventory")}
                            />
                            <label className="form-check-label" htmlFor="trackInventory">
                                Track Inventory
                            </label>
                        </div>
                    </div> */}

                    <div className="mb-3 col-12">
                        <div className="form-label">Image</div>
                        <div className="row">
                            <div className="col-12 col-sm-6 col-md-3">
                                <label htmlFor="option-image-input" className="choosefilenew position-relative">
                                    <input
                                        type="file"
                                        id="option-image-input"
                                        accept="image/*"
                                        hidden
                                        onChange={handleStandaloneImage}
                                    />
                                    <div>
                                        <div className="mb-2">
                                            <i className="bi fs-4 bi-upload" />
                                        </div>
                                        {isUploading ? "Uploading" : "Upload Image"}
                                    </div>
                                </label>
                            </div>
                            {standaloneImage && (
                                <div className="addimagefiles col-12 col-sm-6 col-md-3 mb-2 mt-3 mt-sm-0">
                                    <div className="image-card position-relative">
                                        <img src={standaloneImage} alt="option" className="image-preview" />
                                        <button
                                            type="button"
                                            className="btn btn-danger px-2 py-1 text-white position-absolute"
                                            onClick={removeStandaloneImage}
                                        >
                                            <i className="bi bi-trash3" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <small className="text-muted">Maximum file size: 1MB.</small>
                    </div>

                    <div className="col-12 mt-2 d-grid d-sm-flex justify-content-sm-end">
                        <Button
                            loading={savingStandalone}
                            className="btn btn-primary text-white px-5"
                            type="button"
                            text={isEditing ? "Update Option" : "Add Option"}
                            onClick={handleSubmit(onSubmitStandalone)}
                        />
                    </div>
                </div>
            ) : (
                /* Product linked */
                <div className="row">
                    {isEditingLinked ? (
                        <div className="mb-3 col-12">
                            <label className="form-label">Linked Product</label>
                            <div className="d-flex align-items-center gap-2">
                                {editingOption?.product?.images?.[0] && (
                                    <img
                                        className="flex-shrink-0"
                                        src={editingOption.product.images[0]}
                                        alt=""
                                        style={{ width: 40, height: 40, objectFit: "cover", borderRadius: 6 }}
                                    />
                                )}
                                <span className="fw-semibold text-break">
                                    {editingOption?.product?.title || editingOption?.nameOverride || "Linked product"}
                                </span>
                            </div>
                            <small className="text-muted">
                                The linked product/variant cannot be changed. Edit the overrides below.
                            </small>
                        </div>
                    ) : (
                        <div className="mb-3 col-12">
                            <ProductVariantSelector
                                onSelect={handleVariantSelect}
                                selectedProductId={linkedProduct?.id}
                                selectedProductInfoId={linkedVariant?.id}
                            />
                        </div>
                    )}

                    {((linkedProduct && linkedVariant) || isEditingLinked) && (
                        <>
                            <div className="col-12">
                                <p className="text-muted mb-3">
                                    {isEditingLinked
                                        ? "Edit the override values for this linked option."
                                        : "Values below are auto-filled from the selected variant. You can edit them before saving."}
                                </p>
                            </div>
                            <div className="mb-3 col-12 col-md-6">
                                <label className="form-label">Name Override</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={nameOverride}
                                    onChange={(e) => setNameOverride(e.target.value)}
                                />
                            </div>
                            <div className="mb-3 col-12 col-md-6">
                                <label className="form-label">Price Override (BHD)</label>
                                <input
                                    type="number"
                                    step="any"
                                    className="form-control"
                                    value={priceOverride}
                                    onChange={(e) => setPriceOverride(e.target.value)}
                                    placeholder="0 = Free"
                                />
                            </div>
                            <div className="mb-3 col-12 col-md-6">
                                <label className="form-label">Inventory Quantity</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    value={linkedInventoryQuantity}
                                    onChange={(e) => setLinkedInventoryQuantity(e.target.value)}
                                />
                            </div>
                            <div className="mb-3 col-12 col-md-6">
                                <label className="form-label">Weight</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={linkedWeight}
                                    onChange={(e) => setLinkedWeight(e.target.value)}
                                    placeholder="Auto-filled from selected variant"
                                />
                            </div>
                            <div className="mb-3 col-12 col-md-6">
                                <label className="form-label">Image Override</label>
                                <div className="d-flex align-items-center gap-3">
                                    {imageOverride && (
                                        <img
                                            className="flex-shrink-0"
                                            src={imageOverride}
                                            alt="override"
                                            style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6 }}
                                        />
                                    )}
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={imageOverride}
                                        onChange={(e) => setImageOverride(e.target.value)}
                                        placeholder="Image URL"
                                    />
                                </div>
                            </div>
                            <div className="col-12 mt-2 d-grid d-sm-flex justify-content-sm-end">
                                <Button
                                    loading={savingLinked}
                                    className="btn btn-primary text-white px-5"
                                    type="button"
                                    text={isEditing ? "Update Option" : "Add Option"}
                                    onClick={onSubmitLinked}
                                />
                            </div>
                        </>
                    )}
                </div>
            )}

            <ConfirmationModal {...modalProps} show={showModal} />
        </div>
    );
};

export default ModifierOptions;
